import { ResponseArray, ResponseObject } from '@/types/shared'
import datacentral from '@/utils/axios/datacentral'
import {
    CSCustomerMovementResponse,
    CustomerStatis,
    CustomerStatistical,
    EmployeeIncome,
} from '../types'
import {
    EmployeePerformance,
    SalesTargetFilters,
} from '../components/BusinessTargets/types'
import { ActivityActualsResponse } from '../types'

export const getCustomerStatus = async (filters?: string, group?: string) => {
    const response = await datacentral.get<ResponseArray<CustomerStatistical>>(
        `reports/customers/statistical?${filters ? `filters=${filters}` : ''}${group ? `&group=${group}` : ''}`
    )
    return response?.data?.results
}

export const getCustomerStatusStatistical = async (
    filters?: string,
    group?: string
) => {
    const response = await datacentral.get<CustomerStatis>(
        `reports/customers/summaries?${filters ? `filters=${filters}` : ''}${group ? `&group=${group}` : ''}`
    )
    return response?.data
}

export const getEmployeeIncomes = async (filters?: string) => {
    const response = await datacentral.get<ResponseArray<EmployeeIncome>>(
        `reports/incomes?${filters ? `filters=${filters}` : ''}`
    )
    return response?.data
}

export const getEmployeeDashboard = async (
    filters: SalesTargetFilters,
    employee_id?: number
) => {
    const params = new URLSearchParams()

    const api_endpoint = employee_id
        ? `employees/sales-targets/performance/dashboard/employee`
        : 'employees/sales-targets/performance/dashboard/employees'

    if (filters.employee_id && filters.employee_id.length > 0) {
        // Add employee_ids as array params
        filters.employee_id.forEach((id) => {
            params.append('employee_id', id.toString())
        })
    }

    // Add date range params
    if (filters.gte) {
        params.append('gte', filters.gte)
    }
    if (filters.lte) {
        params.append('lte', filters.lte)
    }

    const response = await datacentral.get<ResponseObject<EmployeePerformance>>(
        `${api_endpoint}?${params.toString()}`
    )
    return response?.data?.result
}

const buildCSCustomerMovementParams = (filters: SalesTargetFilters) => {
    const params = new URLSearchParams()

    filters.employee_id?.forEach((id) => {
        params.append('employee_id[]', id.toString())
    })

    if (filters.product_group_name) {
        params.append('product_group_name[]', filters.product_group_name)
    }

    if (filters.gte) {
        params.append('gte', filters.gte)
    }
    if (filters.lte) {
        params.append('lte', filters.lte)
    }

    return params
}

export const getCSCustomerMovements = async (filters: SalesTargetFilters) => {
    const params = buildCSCustomerMovementParams(filters)
    const response = await datacentral.get<
        ResponseObject<CSCustomerMovementResponse>
    >(`reports/targets-analytics/customer-movements?${params.toString()}`)

    return response?.data?.result
}

export const exportCSCustomerMovementsExcel = async (
    filters: SalesTargetFilters
) => {
    const params = buildCSCustomerMovementParams(filters)
    params.append('export', 'excel')

    const response = await datacentral.get(
        `reports/targets-analytics/customer-movements?${params.toString()}`,
        { responseType: 'blob' }
    )

    return response
}

// Types for employee contains endpoint
export interface EmployeeBasicInfo {
    employee_id: number
    employee_name: string
    employee_email_company: string
    employee_status: number
    department_name: string | null
}

// Get employees by IDs (name and basic info)
export const getEmployeesByIds = async (employeeIds: number[]) => {
    const response = await datacentral.post<ResponseArray<EmployeeBasicInfo>>(
        'employees/contains',
        { employee_ids: employeeIds }
    )
    return response?.data?.results
}

// ============== Product Group Report ==============

// Types for product group report
export interface ProductGroupReportItem {
    product_group_name: string
    total_services: number
    new_services: number
    cancelled_services: number
    lost_revenue: number
    retention_rate: number
    growth_rate: number
}

export interface ProductGroupReportResponse {
    results: ProductGroupReportItem[]
    message: string
}

import {
    ActivityTarget,
    BulkUpsertActivityTargetsPayload,
    GetActivityTargetsParams,
    UpsertActivityTargetPayload,
    EmployeeActivityTargetResponse,
    BulkUpsertSalesTargetsPayload,
    EmployeeSalesTargetResponse,
} from '../types'
import { DataGridPayload } from 'vnetwork-ui/dist/components/GokuDataGrid'

// ============== Activity Targets ==============

export const getActivityTargets = async (
    params: GetActivityTargetsParams,
    employee_id?: number,
    _payload?: DataGridPayload
) => {
    const queryParams = new URLSearchParams()

    const api_endpoint = employee_id
        ? `employees/activity-targets/employee`
        : 'employees/activity-targets'

    if (params.employee_id && params.employee_id.length > 0) {
        params.employee_id.forEach((id) =>
            queryParams.append('employee_id', id.toString())
        )
    }

    if (params.gte) {
        queryParams.append('gte', params.gte)
    }
    if (params.lte) {
        queryParams.append('lte', params.lte)
    }

    const response = await datacentral.get<ResponseArray<ActivityTarget>>(
        `${api_endpoint}?${queryParams.toString()}`
    )
    return response?.data?.results
}

export const upsertActivityTarget = async (
    data: UpsertActivityTargetPayload
) => {
    const response = await datacentral.post<ResponseObject<ActivityTarget>>(
        'employees/activity-targets',
        data
    )
    return response?.data?.result
}

export const bulkUpsertActivityTargets = async (
    data: BulkUpsertActivityTargetsPayload
) => {
    const response = await datacentral.post<ResponseObject<ActivityTarget[]>>(
        'employees/activity-targets/bulk',
        data
    )
    return response?.data?.result
}

// Get activity targets for a single employee (for edit modal)
export const getActivityTargetsByEmployeeId = async (
    employeeId: number,
    gte: string,
    lte: string
) => {
    const queryParams = new URLSearchParams()
    queryParams.append('gte', gte)
    queryParams.append('lte', lte)

    const response = await datacentral.get<
        ResponseObject<EmployeeActivityTargetResponse>
    >(
        `employees/activity-targets/employee/${employeeId}?${queryParams.toString()}`
    )
    return response?.data?.result
}

// ============== Revenue/Sales Targets ==============

export const bulkUpsertSalesTargets = async (
    data: BulkUpsertSalesTargetsPayload
) => {
    const response = await datacentral.post<ResponseObject<any[]>>(
        'employees/sales-targets/bulk-upsert',
        data
    )
    return response?.data?.result
}

export const getSalesTargetsByEmployeeId = async (
    employeeId: number,
    gte: string,
    lte: string
) => {
    const queryParams = new URLSearchParams()
    queryParams.append('gte', gte)
    queryParams.append('lte', lte)

    const response = await datacentral.get<
        ResponseObject<EmployeeSalesTargetResponse>
    >(
        `employees/sales-targets/employee/${employeeId}?${queryParams.toString()}`
    )
    return response?.data?.result
}

// ============== Activity Actuals ==============

export const getEmployeeActivityActuals = async (params: {
    gte: string
    lte: string
    sales_manager_codes?: number[]
}) => {
    const queryParams = new URLSearchParams()
    queryParams.append('gte', params.gte)
    queryParams.append('lte', params.lte)

    if (params.sales_manager_codes && params.sales_manager_codes.length > 0) {
        queryParams.append(
            'sales_manager_codes',
            params.sales_manager_codes.join(',')
        )
    }

    const response = await datacentral.get<ActivityActualsResponse>(
        `reports/bi/customer-pipeline/activities?${queryParams.toString()}`
    )
    return response?.data?.results
}
